import { utf16Length, IsoTimestampSchema } from "./string-validation"
import { z } from "zod"

export const PaymentStatusSchema = z
	.enum(["NOT_STARTED", "PENDING", "AUTHORIZED", "FAILED", "REFUNDED"])
	.meta({ id: "PaymentStatus" })

export type PaymentStatus = z.infer<typeof PaymentStatusSchema>

export const PaymentRefundSchema = z
	.object({
		id: z.uuid(),
		amount: z.number(),
		reason: z.string().optional(),
		providerRefundId: z.string().optional(),
		createdAt: IsoTimestampSchema,
	})
	.meta({ id: "PaymentRefund" })

export type PaymentRefund = z.infer<typeof PaymentRefundSchema>

export const PaymentAttemptSchema = z
	.object({
		id: z.uuid(),
		paymentId: z.uuid(),
		orderId: z.uuid(),
		status: PaymentStatusSchema,
		amount: z.number().optional(),
		provider: z.string().optional(),
		providerRequestId: z.string().optional(),
		providerTransactionId: z.string().optional(),
		createdAt: IsoTimestampSchema,
		updatedAt: IsoTimestampSchema,
	})
	.meta({ id: "PaymentAttempt" })

export type PaymentAttempt = z.infer<typeof PaymentAttemptSchema>

export const PaymentSchema = z
	.object({
		id: z.uuid(),
		orderId: z.uuid(),
		patientId: z.string().optional(),
		patientName: z.string().optional(),
		amount: z.number().optional(),
		refundedAmount: z.number().optional(),
		netAmount: z.number().optional(),
		refundCount: z.number().int().nonnegative().optional(),
		refundedAt: IsoTimestampSchema.optional(),
		refunds: z.array(PaymentRefundSchema).optional(),
		status: PaymentStatusSchema,
		provider: z.string().optional(),
		failureReason: z.string().optional(),
		attempts: z.array(PaymentAttemptSchema).optional(),
		createdAt: IsoTimestampSchema,
		updatedAt: IsoTimestampSchema,
	})
	.meta({ id: "Payment" })

export type Payment = z.infer<typeof PaymentSchema>

export const ManualPaymentApprovalRequestSchema = z
	.strictObject({
		providerTransactionId: z.string().trim().min(1).apply(utf16Length("max", 255)),
		reason: z.string().trim().min(1).apply(utf16Length("max", 10_000)).optional(),
	})
	.meta({ id: "ManualPaymentApprovalRequest" })

export type ManualPaymentApprovalRequest = z.infer<typeof ManualPaymentApprovalRequestSchema>

export const PortalPaymentStatusSchema = z
	.enum(["pending", "authorized", "failed", "refunded"])
	.meta({ id: "PortalPaymentStatus" })

export type PortalPaymentStatus = z.infer<typeof PortalPaymentStatusSchema>

export const PortalPaymentRefundSchema = z
	.strictObject({
		id: z.uuid(),
		amount: z.number().int().nonnegative(),
		reason: z.string().optional(),
		providerRefundId: z.string().optional(),
		createdAt: IsoTimestampSchema,
	})
	.meta({ id: "PortalPaymentRefund" })

export type PortalPaymentRefund = z.infer<typeof PortalPaymentRefundSchema>

export const PortalPaymentAttemptSchema = z
	.strictObject({
		id: z.uuid(),
		paymentId: z.uuid(),
		orderId: z.uuid(),
		status: PortalPaymentStatusSchema,
		amount: z.number().int().nonnegative().optional(),
		provider: z.string().optional(),
		providerRequestId: z.string().optional(),
		providerTransactionId: z.string().optional(),
		createdAt: IsoTimestampSchema,
		updatedAt: IsoTimestampSchema,
	})
	.meta({ id: "PortalPaymentAttempt" })

export type PortalPaymentAttempt = z.infer<typeof PortalPaymentAttemptSchema>

export const PortalPaymentSchema = z
	.strictObject({
		id: z.uuid(),
		orderId: z.uuid(),
		patientId: z.string().optional(),
		patientName: z.string().optional(),
		amount: z.number().int().nonnegative().optional(),
		refundedAmount: z.number().int().nonnegative(),
		netAmount: z.number().int().nonnegative().optional(),
		refundCount: z.number().int().nonnegative(),
		refundedAt: IsoTimestampSchema.optional(),
		refunds: z.array(PortalPaymentRefundSchema).optional(),
		status: PortalPaymentStatusSchema,
		provider: z.string().optional(),
		failureReason: z.string().optional(),
		attempts: z.array(PortalPaymentAttemptSchema).optional(),
		createdAt: IsoTimestampSchema,
		updatedAt: IsoTimestampSchema,
	})
	.meta({ id: "PortalPayment" })

export type PortalPayment = z.infer<typeof PortalPaymentSchema>

export const PaymentFiltersSchema = z
	.object({
		search: z.string().trim().apply(utf16Length("max", 200)).optional(),
		orderId: z.uuid().optional(),
		status: PaymentStatusSchema.optional(),
		page: z.coerce.number().int().positive().default(1),
		limit: z.coerce.number().int().positive().max(100).default(25),
	})
	.meta({ id: "PaymentFilters" })

export type PaymentFilters = z.infer<typeof PaymentFiltersSchema>
